import React, { useEffect, useState } from "react";
import PageTitle from "../../../../../components/ui/PageTitle";
import LoadingIndicator from "../../../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../../../components/ui/ErrorMessage";
import Button from "../../../../../components/ui/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faChartLine,
  faArrowUp,
  faArrowDown
} from "@fortawesome/free-solid-svg-icons";
import {
  faChartBar,
  faUser,
  faMoneyBill1
} from "@fortawesome/free-regular-svg-icons";
import { getComboDashboardSummary } from "../../../../../lib/apiClient";
import { ComboDashboardSummaryResponse } from "../../../../../types";
import { useNavigate, useParams } from "react-router-dom";
import { getErrorMessage } from "../../../../../lib/utils";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
  BarChart,
} from "recharts";
import { ROUTES } from "../../../../../lib/constants";
import Select from "../../../../../components/ui/Select";
import Input from "../../../../../components/ui/Input";
import toast from "react-hot-toast";

const StatCard: React.FC<{
  title: string;
  value: string | number;
  description?: string;
  icon?: React.ReactNode;
  trend?: { value: number; isPositive: boolean };
}> = ({ title, value, description, icon, trend }) => (
  <div className="group bg-gradient-to-br from-dark-secondary to-dark-tertiary p-5 rounded-xl shadow-lg border border-dark-border hover:border-golden-accent/30 transition-all duration-300 hover:shadow-xl hover:shadow-golden-accent/5 min-h-[100px]">
    <div className="flex items-center space-x-4 h-full">
      {icon && (
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-golden-accent rounded-lg flex items-center justify-center text-text-on-accent shadow-lg">
            {icon}
          </div>
        </div>
      )}
      
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <h3 
          className="text-sm font-medium text-text-secondary mb-2 truncate font-montserrat cursor-help" 
          title={title}
        >
          {title}
        </h3>
        
        <p className="text-2xl font-bold text-text-primary group-hover:text-golden-accent transition-colors duration-300 leading-tight font-montserrat mb-1">
          {value}
        </p>
        
        {trend && (
          <div className={`inline-flex items-center text-xs font-medium mt-1 ${
            trend.isPositive 
              ? 'text-functional-success' 
              : 'text-functional-danger'
          }`}>
            <FontAwesomeIcon 
              icon={trend.isPositive ? faArrowUp : faArrowDown} 
              className="mr-1 text-xs" 
            />
            {Math.abs(trend.value)}%
          </div>
        )}
        
        {description && (
          <p className="text-xs text-text-secondary mt-0.5 truncate">{description}</p>
        )}
      </div>
    </div>
  </div>
);

export default function ComboDashboardPage() {
  const router = useNavigate();

  const [summary, setSummary] =
    useState<ComboDashboardSummaryResponse | null>(null);

  const today = new Date();
  const last30DaysStart = new Date(today);
  last30DaysStart.setDate(last30DaysStart.getDate() - 29);
  const [referralCode, setReferralCode] = useState<string | undefined>();

  const formatDefaultDate = (date: Date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState<string>(
    formatDefaultDate(last30DaysStart)
  );
  const [endDate, setEndDate] = useState<string>(
    formatDefaultDate(today)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aggregation, setAggregation] = useState<
    "mon" | "yer" | "day" | "none"
  >("none");

  const aggregationOptions = [
    { val: "none", lab: "Default (Last 30 Days)" },
    { val: "day", lab: "Custom Range" },
    { val: "mon", lab: "Monthly" },
    { val: "yer", lab: "Yearly" },
  ] as const;

  const monthOptions = Array.from(
    { length: new Date().getMonth() + 1 },
    (_, i) => ({
      val: i + 1,
      lab: new Date(0, i)
        .toLocaleString("default", { month: "short" })
        .toUpperCase(),
    })
  );

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2025 + 1 }, (_, i) => {
    const year = 2025 + i;
    return { val: year.toString(), lab: year.toString() };
  });

  const [selectedMon, setSelectedMon] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());

  // inside component
  const { id: comboId } = useParams<{ id: string }>();

  const fetchSummary = async (skipCache = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getComboDashboardSummary(
        comboId!,
        new Date(startDate),
        new Date(endDate),
        aggregation,
        selectedYear,
        selectedMon,
        referralCode,
        skipCache,
      );
      setSummary(data);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(`Failed to load dashboard: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [aggregation, selectedMon, selectedYear, referralCode]);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  return (
    <div className="space-y-6">
      <Button
        onClick={() => router(ROUTES.ADMIN_COMBOS)}
        variant="link"
        size="sm"
        className="mb-1 text-text-secondary hover:text-text-primary"
      >
        ← Back to My Combos
      </Button>
      <PageTitle
        title="Combo Overview"
        subtitle="Analytics dashboard for this combo"
      />

      <div className="bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-primary p-8 md:p-10 rounded-2xl shadow-2xl border border-dark-border backdrop-blur-sm">
        {/* Analytics By - Modern Tabbed control (replaces dropdown) */}
        <div className="mb-8">
          <div className="w-full">
            <div className="flex flex-wrap items-center justify-center sm:justify-start p-1 bg-dark-tertiary rounded-xl border border-dark-border shadow-inner w-full min-w-[220px] sm:min-w-0">
              {aggregationOptions.map((opt, index) => {
                const active = aggregation === opt.val;
                let shortLabel = '';
                if (opt.val === 'none') shortLabel = 'Default';
                else if (opt.val === 'day') shortLabel = 'Range';
                else if (opt.val === 'mon') shortLabel = 'Month';
                else if (opt.val === 'yer') shortLabel = 'Year';
                return (
                  <button
                    key={opt.val}
                    type="button"
                    onClick={() => setAggregation(opt.val)}
                    className={`relative px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-xs sm:text-sm transition-all duration-300 ease-out ${
                      active
                        ? "text-black bg-golden-accent transform scale-105"
                        : "text-text-secondary hover:text-text-primary hover:bg-dark-secondary/50"
                    } ${index === 0 ? "rounded-l-lg" : ""} ${index === aggregationOptions.length - 1 ? "rounded-r-lg" : ""}`}
                  >
                    <span className="inline sm:hidden">{shortLabel}</span>
                    <span className="hidden sm:inline">{opt.lab}</span>
                    {active && (
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-golden-accent/20 to-golden-accent/10 animate-pulse"></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {aggregation === "day" ? (
          <div className="flex flex-wrap items-center gap-4 mb-6 border-b border-dark-tertiary pb-6">
            <Input
              label="Start Date"
              id="start-date"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              disabled={isLoading}
            />
            <Input
              label="End Date"
              id="end-date"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              disabled={isLoading}
            />
            <Button onClick={() => fetchSummary(true)} disabled={isLoading}>
              Apply Filter
            </Button>
          </div>
        ) : aggregation === "mon" ? (
          <>
            <Select
              id="selectedMonth"
              label="Month"
              value={selectedMon || 1}
              onChange={(e) => setSelectedMon(Number(e.target.value))}
              options={monthOptions.map((m) => ({
                value: m.val,
                label: m.lab,
              }))}
              className="bg-dark-secondary text-text-primary"
            />
            <Select
              id="selectedYear"
              label="Year"
              value={selectedYear.toString()}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              options={yearOptions.map((y) => ({
                value: y.val,
                label: y.lab,
              }))}
              className="bg-dark-secondary text-text-primary"
            />
          </>
        ) : aggregation === "yer" ? (
          <Select
            id="selectedYear"
            label="Year"
            value={selectedYear.toString()}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            options={yearOptions.map((y) => ({
              value: y.val,
              label: y.lab,
            }))}
            className="bg-dark-secondary text-text-primary"
          />
        ) : null}

        <div className="flex flex-wrap gap-4 mb-6 items-end">
          <div>
            <label htmlFor="referral" className="block text-text-secondary">
              Filter By Referral Code
            </label>
            <select
              id="referral"
              value={referralCode || ""}
              onChange={(e) => setReferralCode(e.target.value || undefined)}
              className="mt-1 p-2 bg-dark-secondary text-text-primary border border-dark-tertiary rounded"
            >
              <option value="">All Referrals</option>
              {summary?.refContribution?.map((ref) => (
                <option key={ref.refcode || "N/A"} value={ref.refcode}>
                  {ref.refcode || "N/A"}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-10">
            <LoadingIndicator size="lg" text="Loading dashboard data..." />
          </div>
        ) : error ? (
          <div className="py-10">
            <ErrorMessage title="Error Loading Dashboard" message={error} />
          </div>
        ) : summary ? (
          <div className="space-y-6">
            {/* Enhanced Stat Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
              <StatCard
                title="Total Revenue"
                value={`₹${summary.totalRevenue.toFixed(0)}`}
                icon={<FontAwesomeIcon icon={faMoneyBill1} className="text-xl" />}
              />
              <StatCard 
                title="Total Renewals" 
                value={summary.totalRenewals}
                icon={<FontAwesomeIcon icon={faSync} className="text-xl" />}
              />
              <StatCard
                title="Churn Rate"
                value={`${summary.churnRate.toFixed(2)}%`}
                icon={<FontAwesomeIcon icon={faArrowDown} className="text-xl" />}
              />
              <StatCard
                title="Average LTV"
                value={`₹${summary.avgLTV.toFixed(0)}`}
                icon={<FontAwesomeIcon icon={faChartLine} className="text-xl" />}
              />
              <StatCard
                title="Total Subscribers"
                value={summary.totalSubscribers}
                icon={<FontAwesomeIcon icon={faUser} className="text-xl" />}
              />
              <StatCard
                title="Total Subscriptions"
                value={summary.subscriptionCount}
                icon={<FontAwesomeIcon icon={faUser} className="text-xl" />}
              />
            </div>

            {/* Revenue chart */}
            <div className="bg-gradient-to-br from-dark-secondary to-dark-tertiary p-8 rounded-2xl shadow-xl border border-dark-border hover:border-golden-accent/30 transition-all duration-300 mt-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-text-primary flex items-center font-montserrat">
                  <FontAwesomeIcon icon={faChartBar} className="mr-3 text-xl" />
                  {aggregationOptions.find((ag) => ag.val === aggregation)?.lab} Revenue
                </h3>
                <div className="px-3 py-1 bg-functional-success/10 rounded-full text-xs font-medium text-functional-success">
                  Trending Up
                </div>
              </div>
              {summary.dailyRevenue.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={summary.dailyRevenue}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis dataKey="_id" stroke="#999" />
                    <YAxis stroke="#999" />
                    <Tooltip
                      cursor={false}
                      content={({ label, payload }) => {
                        const value = payload?.[0]?.value;
                        return (
                          <div className="p-2 bg-white rounded shadow text-sm text-gray-800">
                            <p>
                              <strong>{aggregation === "yer" ? "Month" : "Date"}:</strong> {label}
                            </p>
                            <p>
                              <strong>Value:</strong> ₹{value?.toFixed(0)}
                            </p>
                          </div>
                        );
                      }}
                      formatter={(value: number) => `₹${value.toFixed(0)}`}
                    />
                    <Bar 
                      dataKey="revenue" 
                      fill="#ffbc06" 
                      barSize={30}
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-text-secondary text-sm py-10 text-center">
                  No revenue data available for this period.
                </p>
              )}
            </div>

            {/* Refcode Contribution */}
            <div className="bg-gradient-to-br from-dark-secondary to-dark-tertiary p-8 rounded-2xl shadow-xl border border-dark-border hover:border-golden-accent/30 transition-all duration-300 mt-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-text-primary flex items-center font-montserrat">
                  <FontAwesomeIcon icon={faChartBar} className="mr-3 text-xl" />
                  Refcode Contribution
                </h3>
                <div className="px-3 py-1 bg-golden-accent/10 rounded-full text-xs font-medium text-golden-accent">
                  Analysis
                </div>
              </div>
              {summary.refContribution.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm text-left text-text-secondary">
                    <thead className="bg-dark-primary">
                      <tr>
                        <th className="px-4 py-2">Refcode</th>
                        <th className="px-4 py-2">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.refContribution.map((ref) => (
                        <tr key={ref.refcode} className="border-b border-dark-tertiary">
                          <td className="px-4 py-2">{ref.refcode}</td>
                          <td className="px-4 py-2">₹{ref.totalRevenue.toFixed(0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-text-secondary text-sm py-10 text-center">No refcode data available for this period.</p>
              )}
            </div>

          </div>
        ) : null}
      </div>
    </div>
  );
}


